// filepath: apps/web/src/editors/form-builder/types.ts
//
// FR-UI-BLD-1 AC-1 / AC-2 / AC-4 — wire types for the form builder SPA.
//
// The wire types mirror the Go server contract at internal/form/handlers.go.
// Field types are the canonical 10 platform-shipped types enum (FR-FORM-1).
// The FetchError union follows the 5-state fetch contract (FR-UI-CORE-10).

export type FieldType =
  | "string"
  | "text"
  | "number"
  | "integer"
  | "float"
  | "boolean"
  | "date"
  | "datetime"
  | "enum"
  | "reference";

export const ALL_FIELD_TYPES: FieldType[] = [
  "string",
  "text",
  "number",
  "integer",
  "float",
  "boolean",
  "date",
  "datetime",
  "enum",
  "reference",
];

export interface FieldDefinition {
  field_type: FieldType;
  label?: string | null;
  placeholder?: string | null;
  required?: boolean;
  visible_when?: string;
  computed_from?: string;
  target_entity?: string;
  enum_options?: string[] | null;
}

export interface FormDraft {
  entity_type: string;
  fields: Record<string, FieldDefinition>;
}

export interface FormCreateRequest {
  entity_type: string;
  fields: Record<string, FieldDefinition>;
}

export interface FormCreateResponse {
  form_id: string;
  version: number;
  entity_type: string;
}

export interface ValidationErrorWire {
  node_id: string;
  type: string;
  field?: string;
  message: string;
}

export interface ValidationErrorEnvelope {
  error: "validation_failed";
  code: "validation_errors";
  errors: ValidationErrorWire[];
}

export interface EntityTypeSummary {
  name: string;
  version: number;
}

export interface FormulaValidateRequest {
  formula: string;
  context: "visibleWhen" | "computedFrom" | "guard" | "delay_expression";
  field_id: string;
}

export interface FormulaValidateResponse {
  ok: boolean;
  type?: string;
  error?: string;
}

export type FormBuilderFetchErrorKind =
  | "not_found"
  | "forbidden"
  | "unauthorized"
  | "network"
  | "validation_failed"
  | "staging_only"
  | "version_conflict"
  | "internal_error";

export interface FormBuilderFetchError {
  kind: FormBuilderFetchErrorKind;
  status: number;
  message: string;
  validationErrors?: ValidationErrorWire[];
}

export function classifyFormBuilderError(
  status: number,
  body: unknown,
): FormBuilderFetchError {
  if (status === 422) {
    const env = body as ValidationErrorEnvelope;
    return {
      kind: "validation_failed",
      status: 422,
      message: env?.error ?? "validation_failed",
      validationErrors: env?.errors ?? [],
    };
  }
  if (status === 403) {
    const error = (body as { error?: string })?.error ?? "forbidden";
    const code = (body as { code?: string })?.code ?? "forbidden";
    if (code === "ENV_PRODUCTION") {
      return { kind: "staging_only", status: 403, message: error };
    }
    return { kind: "forbidden", status: 403, message: error };
  }
  if (status === 404) {
    return { kind: "not_found", status: 404, message: "not_found" };
  }
  if (status === 401) {
    return { kind: "unauthorized", status: 401, message: "unauthorized" };
  }
  if (status === 0) {
    return { kind: "network", status: 0, message: "network" };
  }
  return {
    kind: "internal_error",
    status,
    message: (body as { error?: string })?.error ?? "internal_error",
  };
}
